import { fromEvent as observableFromEvent } from "rxjs";

import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { OverlayPanel, OverlayPanelModule } from "primeng/overlaypanel";
import { DomHandler } from "primeng/dom";
import { TranslateModule } from "@ngx-translate/core";
import { FormsModule } from "@angular/forms";
import { AutofocusDirective } from "../../directives/auto-focus.directive";
import { CommonModule } from "@angular/common";
import { AbstractSigaleComponent } from "../abstract-sigale.component";

@Component({
	standalone: true,
	selector: 'popover',
	templateUrl: './popover.component.html',
	styleUrls: ['./popover.component.scss'],
	imports: [
		CommonModule,
		FormsModule,
		OverlayPanelModule,
		AutofocusDirective,
		TranslateModule
	]
})
export class PopoverComponent extends AbstractSigaleComponent implements OnInit {

	body = document.body;
	@Input('donnees') donnees = [];
	@Input('selections') selections = [];
	@Input('formatter') formatter: Function;
	@Input('filter') filterFunction: Function;
	// Permet de dire si on peut sélectionner plusieurs élément ou pas
	@Input('selectionMultiple') selectionMultiple: boolean = true;

	@Output('onValidation') onValidation: EventEmitter<any> = new EventEmitter<any>();
	@Output('onAnnulation') onAnnulation: EventEmitter<any> = new EventEmitter<any>();

	@ViewChild('popover', {static: true}) popover: OverlayPanel;
	@ViewChild('filtreRef', {static: true}) filtreRef;

	init = true;
	filtre: string = '';
	selectAll: boolean = false;
	selectionMultipleInterne: boolean = true;
	selectionsInterne: Array<any> = [];
	donneesInterne: Array<any> = [];
	refreshed = false;

	constructor() {
		super();
	}

	ngOnInit() {
		super.ngOnInit();
		if (this.filtreRef) {
			observableFromEvent(this.filtreRef.nativeElement, 'keyup').pipe(
							// Récupération de la valeur
							map((evt: any) => evt.target.value),
							// On attends 300 ms avant de filtrer
							debounceTime(300),
							distinctUntilChanged(),)
					.subscribe((text: string) => this.filtrer(text));
		}
	}

	ngAfterViewChecked() {
		// S'il y a besoin de repositionner (lorsqu'il y a un changement de données par exemple) la popover on la repositionne
		if (this.popover && this.popover.target && this.popover.container) {
			if (this.refreshed) {
				DomHandler.absolutePosition(this.popover.container, this.popover.target);
				this.refreshed = false;
			}
		}
	}

	/**
	 * Annulation du filtre
	 */
	clearFilter() {
		this.filtre = '';
		this.filtrer(this.filtre);
	}

	/**
	 * Filtre les données
	 * @param filtre
	 */
	filtrer(filtre) {
		if (this.filterFunction) {
			this.donneesInterne.forEach(donnee => {
				donnee.hide = !this.filterFunction(donnee, filtre);
			});
		}
	}

	/**
	 * Ajoute ou supprime la donnée de la liste des données sélectionnées
	 * @param $event
	 * @param donnee la donnée cochée/décoché
	 */
	toggleDonnee($event, donnee) {
		if (!this.selectionsInterne) {
			this.selectionsInterne = [];
		}

		let self = this;
		// Recherche de l'index de l'élément dans la liste de sélection
		let index = this.selectionsInterne?.findIndex((item) => {
			return self.formatter(item) == self.formatter(donnee);
		})

		// Si on a coché et que l'on est en sélection multiple
		if ($event.target.checked && this.selectionMultiple) {
			// l'élément n'est pas présent dans la liste on l'ajoute
			if (index === -1) {
				this.selectionsInterne.push(donnee);
			}
		}
		else {
			// Si l'élément a été trouvé on le supprime de la sélection
			if (index != -1) {
				this.selectionsInterne.splice(index, 1);
			}
		}
		this.updateSelectAll();
	}

	/**
	 * Sélectionne/Désélectionne toutes les données
	 * @param $event
	 */
	selectDeselectAll($event) {
		this.selectionsInterne = [];
		this.donneesInterne.forEach(donnee => {
			if ($event.target.checked) {
				this.selectionsInterne.push(donnee);
			}
			donnee.selected = $event.target.checked;
		});
	}

	/**
	 * Raffraichissement des données internes de la popover
	 */
	refresh() {
		//force le focus sur la recherche
		this.init = true;
		// liste des données sélectionnées
		let donneesSelected = [];

		this.reinitialiser();
		this.selectionMultipleInterne = this.selectionMultiple;

		if (this.donnees && this.donnees.length) {
			this.donneesInterne = Object.assign([], this.donnees);
			this.donneesInterne.forEach((donnee) => {
				donnee.selected = false;
			});

			if (this.selections && this.selections.length) {
				this.selectionsInterne = Object.assign([], this.selections);

				this.donneesInterne.forEach((donnee) => {
					for (let selection of this.selectionsInterne) {
						if (this.formatter(selection) === this.formatter(donnee)) {
							donnee.selected = true;
							donneesSelected.push(donnee);
						}
						// Si on est en sélection unique et que l'on vient de trouver une sélection, on sort de la boucle
						// cela ne sert à rien de continuer à chercher les sélections
						if (!this.selectionMultipleInterne && donnee.selected) {
							return;
						}
					}
				});

				// recréer la sélection interne afin que les objets sélectionnées match réellement les données interne en terme de référence objet. Dans le cas contraire, on se retrouve avec des valeurs
				// théoriquement sélectionnées qui n'apparaissait pas telle quel dans la popover
				this.selectionsInterne = this.donneesInterne.filter(valeur => valeur.selected);
			}

			// Mise à jour du bouton de sélection complète
			this.updateSelectAll();
		}

		// Si tout n'est pas sélectionné, il faut mettre les champs sélectionnés en premier
		if (!this.selectAll) {
			// index permettant de savoir à quelle niveau il faut inisérer les données sélectionnées
			let positionIndexDonneeAAjouter: number = 0;
			donneesSelected.forEach(donnee => {
						let index = this.donneesInterne.indexOf(donnee);
						// On enlève la donnée sélectionnée
						this.donneesInterne.splice(index, 1);
						// pour la mettre en début de tableau à positionIndexDonneeAAjouter pour garder l'odre originel dans les sélections
						this.donneesInterne.splice(positionIndexDonneeAAjouter++, 0, donnee);
					}
			);
		}
		this.clearFilter();
		// évite de bloquer l'utilisateur sur l'input
		//quand l'input est affiché init est remit a false

		setTimeout(() => {
			this.init = false;
			this.refreshed = true;
		}, 0);
	}

	/**
	 * Gère la coche automatique du bouton "Sélectionner tout"
	 */
	private updateSelectAll() {
		if (this.donneesInterne && this.donneesInterne.length) {
			if (this.selectionMultipleInterne && (this.donneesInterne || this.selectionsInterne)) {
				this.selectAll = this.donneesInterne.length === this.selectionsInterne.length;
			}
		}
	}

	/**
	 * Ajoute un élément sélectionnable dans la popover
	 * @param element
	 */
	addElement(element: any) {
		this.donneesInterne.push(element);
		this.updateSelectAll();
	}

	/**
	 * Affiche la popover
	 * @param event
	 * @param target cible
	 */
	show(event) {
		if (!this.popover.overlayVisible) {
			event.stopPropagation();
		}
		this.popover.toggle(event, event.target);
	}

	/**
	 * Cache la popover
	 */
	hide() {
		this.popover.hide();
	}

	/**
	 * Gestion de l'évènement de validation de la popover
	 * @param $event
	 */
	handleValidation($event) {
		this.onValidation.emit(this.selectionsInterne);
		this.popover.hide();
	}

	/**
	 * Gestion de l'évènement d'annulation de la popover
	 * @param $event
	 */
	handleAnnulation($event) {
		this.onAnnulation.emit($event);
		this.popover.hide();
	}

	/**
	 * Gestion de l'évènement de fermeture de la popover
	 */
	reinitialiser() {
		this.selectionMultipleInterne = true;
		this.selectionsInterne = [];
		this.donneesInterne = [];
		this.selectAll = false;
		this.clearFilter();
	}

	traductionFR(): any {
		return {
			"SIGALE_ANGULAR": {
				"COMPONENTS": {
					"POPOVER": {
						"FILTRE_LABEL": "Rechercher",
						"BOUTON_VALIDATION": "OK",
						"BOUTON_ANNULATION": "Annuler",
						"CHECKBOX_SELECTIONNER_TOUT": "Sélectionner tout"
					}
				}
			}
		};
	}


	traductionIT(): any {
		return {
			"SIGALE_ANGULAR": {
				"COMPONENTS": {
					"POPOVER": {
						"FILTRE_LABEL": "Ricercare",
						"BOUTON_VALIDATION": "OK",
						"BOUTON_ANNULATION": "Annullare",
						"CHECKBOX_SELECTIONNER_TOUT": "Selezionare tutto"
					}
				}
			}
		};
	}
}
